import { useQuery } from '@tanstack/react-query';
import { apiClient } from 'api';
import { useToast } from 'hooks/useToast';
import { useEffect, useRef } from 'react';

interface DomainKnowledgeResult {
  question: string;
  question_id: number | null;
  question_content: string | null;
  question_type?: string;
  answer: string;
  score?: number;
  correct: boolean;
  points_earned: number;
  points_possible: number;
  reason?: string | null;
  score_reason?: string | null;
  feedback?: string | null;
}

export interface DomainKnowledgeData {
  score: number;
  percentage: number;
  auto_scored: boolean;
  total_points_earned: number;
  total_points_possible: number;
  // Legacy field names (backwards compat)
  total_score_gotten?: number;
  total_score_obtained?: number;
  results: DomainKnowledgeResult[];
  version?: number;
}

interface DomainKnowledgeResponse {
  message: string;
  data: DomainKnowledgeData;
}

/**
 * Hook for fetching domain knowledge data
 */
export const useDomainKnowledge = (reportId?: string) => {
  const { openToast } = useToast();
  const hasShownError = useRef(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['domainKnowledge', reportId],
    queryFn: async () => {
      if (!reportId) {
        throw new Error('Report ID is required');
      }

      const response = await apiClient.get<DomainKnowledgeResponse>(
        `/reports/${reportId}/domain-knowledge/`
      );
      return response.data.data;
    },
    enabled: !!reportId,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (error && !hasShownError.current) {
      hasShownError.current = true;
      const errorMessage = (error as any).response?.data?.message || (error as any).message || 'Failed to fetch domain knowledge';
      openToast({
        show: true,
        heading: "Error",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    }
    
    if (!error) {
      hasShownError.current = false;
    }
  }, [error, openToast]);

  return {
    domainKnowledge: data,
    isLoading,
    error,
    refetch,
  };
};
